import { FC, useEffect, useMemo, useState } from 'react';
import bbox from '@turf/bbox';
import { atom, useAtomValue } from 'jotai';
import { Layer, Source, type MapRef } from 'react-map-gl';

import MapboxMap from 'components/map/map';
import { mapColors } from 'components/map/map-colors';

import { packageMapConfigAtom } from './atoms';

import styles from './ltd-map.module.scss';

const DEFAULT_CENTER = { longitude: -101.49, latitude: 32.32 };
const DEFAULT_ZOOM = 9;

const SEARCH_AREA_FILL = '#f1c21b';
const SELECTED_UNIT_FILL = mapColors.scopedAssetsBorder;

/** Props for {@link LtdMap}. */
export interface LtdMapProps {
  /**
   * Polygon for the user's search scope — survey area or unit boundary.
   * Highlighted in yellow as a "show me what I asked for" indicator.
   * Null when the user hasn't picked anything yet.
   */
  searchPolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  /**
   * Polygon of the unit that contains the user's currently-selected
   * slot. Highlighted blue. Null until the user picks a slot.
   */
  unitPolygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
}

/**
 * Lightweight Mapbox map for the WBA Add LTD modal (INS-9040). Paints
 * two polygons — the user's search area (yellow) and the parent unit of
 * the user's selected slot (blue) — and auto-fits to whichever polygons
 * are present.
 *
 * Distinct from `<PackageWizardMap>` (which paints draft-features for
 * Step 1's slot/asset overlays); the wizard map is too tightly coupled
 * to the draft-features atom shape to reuse here. Reuses the existing
 * `packageMapConfigAtom` so the basemap selection / 3D toggle stay
 * consistent with the rest of the wizard.
 *
 * @param {LtdMapProps} props - Search polygon + selected-unit polygon
 * @return {JSX.Element} The LTD modal map
 */
export const LtdMap: FC<LtdMapProps> = ({ searchPolygon, unitPolygon }) => {
  const [refAtom] = useState(() => atom<MapRef | null>(null));
  const mapRef = useAtomValue(refAtom);

  const searchCollection = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: searchPolygon
        ? [{ type: 'Feature', geometry: searchPolygon, properties: {} }]
        : [],
    }),
    [searchPolygon]
  );
  const unitCollection = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: unitPolygon
        ? [{ type: 'Feature', geometry: unitPolygon, properties: {} }]
        : [],
    }),
    [unitPolygon]
  );

  // Auto-fit on polygon change. Search polygon fills the frame when set;
  // unit polygon nudges the camera in tighter once the user picks a slot.
  useEffect(() => {
    if (!mapRef) return;
    const target = unitPolygon ?? searchPolygon;
    if (!target) return;
    const bounds = bbox({ type: 'Feature', geometry: target, properties: {} });
    if (bounds.some((coord) => !Number.isFinite(coord))) return;
    mapRef.getMap().fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 32, duration: 500 }
    );
  }, [mapRef, searchPolygon, unitPolygon]);

  return (
    <div className={styles.container}>
      <MapboxMap
        longitude={DEFAULT_CENTER.longitude}
        latitude={DEFAULT_CENTER.latitude}
        zoom={DEFAULT_ZOOM}
        mapConfigAtom={packageMapConfigAtom}
        refAtom={refAtom}
        disableWellsLayer
      >
        <Source id="ltd-search-area" type="geojson" data={searchCollection}>
          <Layer
            id="ltd-search-area-fill"
            type="fill"
            paint={{
              'fill-color': SEARCH_AREA_FILL,
              'fill-opacity': 0.25,
            }}
          />
          <Layer
            id="ltd-search-area-line"
            type="line"
            paint={{
              'line-color': SEARCH_AREA_FILL,
              'line-width': 2,
            }}
          />
        </Source>
        <Source id="ltd-selected-unit" type="geojson" data={unitCollection}>
          <Layer
            id="ltd-selected-unit-fill"
            type="fill"
            paint={{
              'fill-color': SELECTED_UNIT_FILL,
              'fill-opacity': 0.2,
            }}
          />
          <Layer
            id="ltd-selected-unit-line"
            type="line"
            paint={{
              'line-color': SELECTED_UNIT_FILL,
              'line-width': 2,
            }}
          />
        </Source>
      </MapboxMap>
    </div>
  );
};
